import React from "react";
import { RiskFactorsCard, TRANSLATIONS as RiskFactorsCardTranslations } from "../riskFactorsCard/riskFactorsCard";
import { TRANSLATIONS as RiskFactorsPillTranslations } from "../riskFactorsPill/riskFactorsPill";
import { IRiskFactorDetail, shouldDisplayTailgating } from "../../helpers/transformRiskFactors";
import { InfoModal } from "../infoModal/infoModal";
import { IDriverSafetyDataStore } from "../../../store/types";

import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { getDriverSafetyModalClassNames } from "../../utils/driverSafetyModalClassNames";

const INFO_MODAL_TITLE = "Your Performance";
const PERFORMANCE_MESSAGE = "Your collision risk is based on how you perform in these risk factors.";
const PERFORMANCE_METRIC_TEMPLATE = "Being a {Top Performer} decreases your chance of a collision in the next 100,000 km.";
const PERFORMANCE_IMPERIAL_TEMPLATE = "Being a {Top Performer} decreases your chance of a collision in the next 100,000 mi.";
const TOP_PERFORMER = "Top Performer";
const TOP_PERFORMERS = "Top Performers";
const SPEEDING = "Speeding";
const SPEEDING_DESCRIPTION = "Driving faster than the posted speed limit.";
const BRAKING = "Braking";
const BRAKING_DESCRIPTION = "Applying the brakes suddenly and forcefully, in a way that may be unsafe or cause discomfort.";
const ACCELERATION = "Acceleration";
const ACCELERATION_DESCRIPTION = "Quickly increasing speed in a short time, in a way that may be unsafe or cause discomfort.";
const CORNERING = "Cornering";
const CORNERING_DESCRIPTION = "Taking a turn at a high speed, in a way that may be unsafe or cause discomfort.";
const TAILGATING = "Tailgating";
const TAILGATING_DESCRIPTION = "An asset is following another vehicle too closely. Close following distance reduces time to react to sudden braking.";

const getPerformanceModalText = (isMetric: boolean, mostRecentInsight: IDriverSafetyDataStore | null) => {
    const { translate } = useTranslationContext();
    const performanceTemplate = translate(isMetric ? PERFORMANCE_METRIC_TEMPLATE : PERFORMANCE_IMPERIAL_TEMPLATE);
    const performanceMessageParts = performanceTemplate.split("{Top Performer}");

    const isTailgatingDisplayed = shouldDisplayTailgating(mostRecentInsight);

    const { driverSafetyModalTextFactorClassName, driverSafetyModalTextMessageClassName } = getDriverSafetyModalClassNames();

    return (
        <div className="driver-safety-modal-text">
            <div className={driverSafetyModalTextMessageClassName}>
                {translate(PERFORMANCE_MESSAGE)} {performanceMessageParts[0]}
                <b>{translate(TOP_PERFORMER)}</b>
                {performanceMessageParts[1]}
            </div>
            <div className={driverSafetyModalTextFactorClassName}>
                <b>{translate(SPEEDING)}</b>
                <span>{translate(SPEEDING_DESCRIPTION)}</span>
            </div>
            <div className={driverSafetyModalTextFactorClassName}>
                <b>{translate(BRAKING)}</b>
                <span>{translate(BRAKING_DESCRIPTION)}</span>
            </div>
            <div className={driverSafetyModalTextFactorClassName}>
                <b>{translate(ACCELERATION)}</b>
                <span>{translate(ACCELERATION_DESCRIPTION)}</span>
            </div>
            <div className={driverSafetyModalTextFactorClassName}>
                <b>{translate(CORNERING)}</b>
                <span>{translate(CORNERING_DESCRIPTION)}</span>
            </div>
            {isTailgatingDisplayed && (
                <div className={driverSafetyModalTextFactorClassName}>
                    <b>{translate(TAILGATING)}</b>
                    <span>{translate(TAILGATING_DESCRIPTION)}</span>
                </div>
            )}
        </div>
    );
};

interface IRiskFactorSection {
    riskFactorDetails: IRiskFactorDetail[];
    driverSafetyInsights: IDriverSafetyDataStore[];
}

export const RiskFactorsSection: React.FC<IRiskFactorSection> = ({ riskFactorDetails, driverSafetyInsights }) => {
    const { translate } = useTranslationContext();

    let currentUser = window.driveApp.userService.getCurrentUser();

    const isMetric = currentUser?.isMetric ?? false;
    const mostRecentInsight = driverSafetyInsights && driverSafetyInsights.length > 0
        ? driverSafetyInsights[driverSafetyInsights.length - 1]
        : null;

    return (
        <div className="risk-factors-section">
            <div className="risk-factors-section__content">
                <div className="risk-factors-section__title">
                    <span>{translate(INFO_MODAL_TITLE)}</span>
                    <div className="risk-factors-section__title__info-modal">
                        <InfoModal title={translate(INFO_MODAL_TITLE)} content={getPerformanceModalText(isMetric, mostRecentInsight)} />
                    </div>
                </div>

                <RiskFactorsCard riskFactors={riskFactorDetails} />
                <div className="risk-factors-legend-container">
                    <svg className="svgIcon geotabButtonIcons benchmarkBar__icon"><use xlinkHref="#drive-safety-benchmark-triangle"></use></svg>
                    <div className="risk-factors-legend-container__title">{translate(TOP_PERFORMERS)}</div>
                </div>
            </div>
        </div>
    );
};


export const TRANSLATIONS: string[] = [
    INFO_MODAL_TITLE,
    PERFORMANCE_MESSAGE,
    PERFORMANCE_IMPERIAL_TEMPLATE,
    PERFORMANCE_METRIC_TEMPLATE,
    TOP_PERFORMER,
    TOP_PERFORMERS,
    SPEEDING,
    SPEEDING_DESCRIPTION,
    BRAKING,
    BRAKING_DESCRIPTION,
    ACCELERATION,
    ACCELERATION_DESCRIPTION,
    CORNERING,
    CORNERING_DESCRIPTION,
    TAILGATING,
    TAILGATING_DESCRIPTION
]
    .concat(RiskFactorsPillTranslations || [])
    .concat(RiskFactorsCardTranslations || []);
